import React, { useContext, useEffect } from 'react';

import { useForm, useFormState } from 'react-final-form';

import { ProjectIssueTypesContext } from '@qality/common-components/src/lib/ProjectIssueTypesProvider';
import { useTestCyclesContext } from '@qality/common-components/src/lib/TestCyclesProvider';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { TestCase, TestCycle } from '@qality/common-model';

import { useAddTestCasesContext } from '../../../../lib/AddTestCasesProvider/AddTestCasesContext';
import { SelectCycleStepWrapperStyled } from './SelectCycle.styled';
import { SelectCycleView } from './SelectCycle.view';

export type TestCycleOption = {
  label: string;
  value: number;
  projectId: number | string;
  testCasesCount: number;
};

export type TestCycleData = {
  name: string;
  id: number;
  project: { id: number };
  testCases: Array<TestCase>;
};

const formatTestCycleToOption = (
  testCycles: Array<TestCycle>
): Array<TestCycleOption> => {
  const options = testCycles.map((testCycleData) => {
    return {
      label: `(${testCycleData.project.key}) ${testCycleData.name}`,
      value: testCycleData.id,
      projectId: testCycleData.project.id,
      testCasesCount: testCycleData.testCases?.length,
    };
  });

  return options;
};

const formatProjectToOption = (projects) => {
  const options = projects?.map(
    ({ name = '', id: projectId = '', key: projectKey = '' } = {}) => {
      return {
        label: name,
        value: projectId,
        key: projectKey,
      };
    }
  );

  return options;
};

type Props = {
  onCancel: () => void;
};

export const SelectCycle = ({ onCancel }: Props): JSX.Element => {
  const { change } = useForm();
  const {
    values: { testCycle: testCycleFieldValue, project },
  } = useFormState();

  const { availableProjects, availableIssues, isFetchingIssues } =
    useAddTestCasesContext();
  const { fetchIssueTypes } = useContext(ProjectIssueTypesContext);

  const {
    testCycles,
    fetchTestCycles,
    isLoading: isLoadingTestCycles,
  } = useTestCyclesContext();

  const hasNoTestCases = !isFetchingIssues && availableIssues?.length <= 0;

  const loadOptions = async (query): Promise<Array<TestCycleOption>> => {
    const newTestCycles = await fetchTestCycles(
      project?.value,
      query,
      0,
      null,
      false
    );
    return formatTestCycleToOption(newTestCycles);
  };

  useEffect(() => {
    if (project?.key) {
      if (testCycleFieldValue?.projectId !== project.value) {
        change('testCycle', null);
      }

      fetchIssueTypes(project.key);
      // when projecy.key exist, then project.id also exist
      fetchTestCycles(project.value, undefined, undefined, undefined, false);
    } else {
      change('testCycle', null);
    }
  }, [project?.key]);

  return (
    <SelectCycleStepWrapperStyled>
      {isFetchingIssues ? (
        <SpinnerLoader
          size='large'
          shouldHideContent
          text='We are loading neccessary data'
        />
      ) : (
        <SelectCycleView
          onCancel={onCancel}
          noTestCases={hasNoTestCases}
          testCycleOptions={formatTestCycleToOption(testCycles)}
          isLoadingTestCycles={isLoadingTestCycles}
          loadOptions={loadOptions}
          projectOptions={formatProjectToOption(availableProjects)}
          isTestCycleDropdownDisabled={!project?.key}
        />
      )}
    </SelectCycleStepWrapperStyled>
  );
};
